import { useState } from 'react';
import styled from 'styled-components';
import tw from 'twin.macro';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faStar } from '@fortawesome/free-solid-svg-icons';

const Container = styled.div`
  ${tw`flex flex-col items-center gap-2`}
`;

const StarsContainer = styled.div`
  ${tw`flex gap-2`}
`;

const StarButton = styled.button<{ $isSelected: boolean; $isHovered: boolean }>`
  ${tw`text-4xl transition-colors duration-150 cursor-pointer border-0 bg-transparent`};
  color: ${({ $isSelected, $isHovered }) => ($isSelected || $isHovered ? '#C9B896' : '#E5E7EB')};

  &:hover {
    transform: scale(1.1);
  }
`;

const RatingLabel = styled.span`
  ${tw`text-lg font-medium text-text-secondary`}
`;

interface StarRatingProps {
  rating: number;
  onRatingChange: (rating: number) => void;
  size?: 'sm' | 'md' | 'lg';
  showLabel?: boolean;
}

const RATING_LABELS: { [key: number]: string } = {
  1: 'Poor',
  2: 'Fair',
  3: 'Good',
  4: 'Very Good',
  5: 'Great',
};

export const StarRating = ({
  rating,
  onRatingChange,
  size = 'lg',
  showLabel = true,
}: StarRatingProps) => {
  const [hoveredRating, setHoveredRating] = useState<number>(0);

  const getSizeClass = () => {
    switch (size) {
      case 'sm':
        return 'text-xl';
      case 'md':
        return 'text-2xl';
      case 'lg':
      default:
        return 'text-4xl';
    }
  };

  const displayRating = hoveredRating || rating;
  const label = displayRating ? RATING_LABELS[displayRating] : '';

  return (
    <Container>
      <StarsContainer>
        {[1, 2, 3, 4, 5].map(star => (
          <StarButton
            key={star}
            type="button"
            $isSelected={star <= rating}
            $isHovered={star <= hoveredRating}
            className={getSizeClass()}
            onClick={() => onRatingChange(star)}
            onMouseEnter={() => setHoveredRating(star)}
            onMouseLeave={() => setHoveredRating(0)}
          >
            <FontAwesomeIcon icon={faStar} />
          </StarButton>
        ))}
      </StarsContainer>
      <RatingLabel>{showLabel && label ? label : '\u00A0'}</RatingLabel>
    </Container>
  );
};
